import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Factory, Plus, SearchX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AdminPage, EmptyState, PillTabs, SearchField, formatNumber } from '../../../components/design-system/admin';
import { networkErrorMessage, useNetworkAdminApi } from '../../../common/hooks/use-network-admin-api.hook';
import { NETWORK_BRAND_STATUSES, type BrandRow } from '../../../common/hooks/network-admin.types';
import type { NetworkBrandStatus } from '../../../common/models/network.model';
import { NewBrandDialog } from '../../../modules/superadmin/network/NewBrandDialog';
import { BrandStatusBadge } from '../../../modules/superadmin/network/network-parts';
import { vocabKey } from '../../../modules/superadmin/network/network-helpers';

type StatusFilter = 'all' | NetworkBrandStatus;
const SEARCH_DEBOUNCE_MS = 300;

/** Every manufacturer in the network: partners, claimed and unclaimed brands, with how much of each is in. */
export function NetworkBrandsPage() {
    const { t } = useTranslation();
    const api = useNetworkAdminApi();
    const navigate = useNavigate();
    const [status, setStatus] = useState<StatusFilter>('all');
    const [search, setSearch] = useState('');
    const [q, setQ] = useState('');
    const [rows, setRows] = useState<BrandRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const seq = useRef(0);

    useEffect(() => {
        const id = window.setTimeout(() => setQ(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(id);
    }, [search]);

    const load = useCallback(async () => {
        const mine = ++seq.current;
        setLoading(true);
        setError(null);
        try {
            const items = await api.brands({ q: q || undefined, status: status === 'all' ? undefined : status });
            if (mine === seq.current) setRows(items);
        } catch (e) {
            if (mine !== seq.current) return;
            setRows([]);
            setError(networkErrorMessage(e) || t('ADMIN_SHELL.COMMON.LOAD_FAILED'));
        } finally {
            if (mine === seq.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, q, status]);

    useEffect(() => {
        load();
    }, [load]);

    const tabs = [
        { value: 'all' as StatusFilter, label: t('ADMIN_SHELL.COMMON.ALL') },
        ...NETWORK_BRAND_STATUSES.slice().reverse().map((s) => ({ value: s as StatusFilter, label: t(`ADMIN_NETWORK.BRAND_STATUS.${vocabKey(s)}`) })),
    ];

    return (
        <AdminPage
            title={t('ADMIN_NETWORK.NAV_BRANDS')}
            parents={[{ label: t('ADMIN_SHELL.NAV.SECTION_CATALOG') }, { label: t('ADMIN_NETWORK.NAV'), href: '/network' }]}
            description={t('ADMIN_NETWORK.BRANDS.DESCRIPTION')}
            actions={
                <Button variant="outline" onClick={() => setCreating(true)} data-testid="new-brand">
                    <Plus />
                    {t('ADMIN_NETWORK.NEW_BRAND.TITLE')}
                </Button>
            }
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <PillTabs value={status} options={tabs} onChange={setStatus} ariaLabel={t('ADMIN_NETWORK.FIELDS.STATUS')} />
                <SearchField value={search} onChange={setSearch} placeholder={t('ADMIN_NETWORK.BRANDS.SEARCH')} />
            </div>

            <Card className="gap-0 overflow-hidden p-0" data-testid="brands-list">
                {loading && !rows.length ? (
                    <div className="grid gap-2 p-4" data-testid="brands-loading">
                        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-11 w-full" />)}
                    </div>
                ) : error ? (
                    <EmptyState
                        title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
                        description={error}
                        action={<Button variant="outline" onClick={load}>{t('ADMIN_SHELL.COMMON.RETRY')}</Button>}
                    />
                ) : !rows.length ? (
                    q || status !== 'all' ? (
                        <EmptyState icon={<SearchX />} title={t('ADMIN_NETWORK.BRANDS.EMPTY_SEARCH_TITLE')} description={t('ADMIN_NETWORK.BRANDS.EMPTY_SEARCH_BODY')} />
                    ) : (
                        <EmptyState
                            icon={<Factory />}
                            title={t('ADMIN_NETWORK.BRANDS.EMPTY_TITLE')}
                            description={t('ADMIN_NETWORK.BRANDS.EMPTY_BODY')}
                            action={<Button variant="outline" onClick={() => setCreating(true)}>{t('ADMIN_NETWORK.NEW_BRAND.TITLE')}</Button>}
                        />
                    )
                ) : (
                    <div className="overflow-x-auto">
                        <Table data-testid="brands-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="ps-4">{t('ADMIN_NETWORK.FIELDS.BRAND')}</TableHead>
                                    <TableHead>{t('ADMIN_NETWORK.FIELDS.STATUS')}</TableHead>
                                    <TableHead className="text-end">{t('ADMIN_NETWORK.COVERAGE.COLLECTIONS')}</TableHead>
                                    <TableHead className="text-end">{t('ADMIN_NETWORK.COVERAGE.PRODUCTS')}</TableHead>
                                    <TableHead className="text-end">{t('ADMIN_NETWORK.COVERAGE.PUBLISHED')}</TableHead>
                                    <TableHead className="pe-4 text-end">{t('ADMIN_NETWORK.COVERAGE.RETAILERS')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((b) => (
                                    <TableRow key={b._id} className="cursor-pointer" onClick={() => navigate(`/network/brands/${b._id}`)} data-testid={`brand-row-${b.slug || b._id}`}>
                                        <TableCell className="max-w-[320px] ps-4">
                                            <div className="truncate font-medium">{b.name}</div>
                                            <div className="truncate text-xs text-muted-foreground">
                                                {[b.country, b.website?.replace(/^https?:\/\//, ''), b.aliases?.length > 1 ? t('ADMIN_NETWORK.BRANDS.ALIASES_N', { count: b.aliases.length - 1 }) : null]
                                                    .filter(Boolean)
                                                    .join(' · ') || b.slug}
                                            </div>
                                        </TableCell>
                                        <TableCell><BrandStatusBadge status={b.status} /></TableCell>
                                        <TableCell className="text-end tabular-nums">{formatNumber(b.counts?.collections ?? 0)}</TableCell>
                                        <TableCell className="text-end tabular-nums">{formatNumber(b.counts?.products ?? 0)}</TableCell>
                                        <TableCell className="text-end tabular-nums">{formatNumber(b.counts?.published ?? 0)}</TableCell>
                                        <TableCell className="pe-4 text-end tabular-nums">{formatNumber(b.counts?.retailers ?? 0)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            <NewBrandDialog open={creating} onOpenChange={setCreating} onCreated={(b) => navigate(`/network/brands/${b._id}`)} />
        </AdminPage>
    );
}

export default NetworkBrandsPage;
